import { Navigate } from 'react-router-dom';
import { isStaff, tierDirection, tierLabel, type Tier } from '@foundry/shared';
import { useMe } from '../api/auth.js';
import {
  useCancelMembership,
  useChangeTier,
  useMembership,
  useResumeMembership,
} from '../api/membership.js';
import type { Membership, TierCatalogEntry } from '../api/types.js';
import { Alert, Button, Card, Spinner } from '../components/ui.js';
import { useConfirm } from '../components/ConfirmDialog.js';

/** The founder's self-service membership area: compare plans, upgrade, downgrade, cancel. */
export function MembershipPage() {
  const { data: user } = useMe();
  const q = useMembership();
  const change = useChangeTier();
  const cancel = useCancelMembership();
  const confirm = useConfirm();
  const resume = useResumeMembership();

  // Membership is a founder concept; staff manage tiers from the console instead.
  if (user && isStaff(user.role)) return <Navigate to="/console" replace />;

  if (q.isPending) return <Spinner />;
  if (q.isError || !q.data) return <Alert>Could not load your membership.</Alert>;
  const m = q.data;
  const busy = change.isPending || cancel.isPending || resume.isPending;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="font-title text-3xl tracking-wide">Membership</h1>
        <p className="opacity-70">
          You’re on the <span className="font-medium">{tierLabel(m.tier)}</span> plan. Change or
          cancel it below.
        </p>
      </div>

      {m.pending ? (
        <PendingBanner pending={m.pending} disabled={busy} onUndo={() => resume.mutate()} />
      ) : null}

      <div className="grid gap-4 sm:grid-cols-3">
        {m.catalog.map((entry) => (
          <PlanCard
            key={entry.tier}
            entry={entry}
            current={m.tier}
            pendingTier={m.pending && !m.pending.cancelAtPeriodEnd ? m.pending.tier : null}
            disabled={busy}
            onChoose={() => change.mutate(entry.tier)}
          />
        ))}
      </div>

      <p className="text-sm opacity-70">
        Upgrades and downgrades take effect on the 1st of next month. Need it sooner?{' '}
        <span className="font-medium">Contact support</span> and we’ll arrange it.
      </p>

      <Card>
        <h2 className="mb-1 font-title text-xl tracking-wide">Cancel membership</h2>
        <p className="mb-3 text-sm opacity-70">
          Your access continues until the end of the current period. On the 1st your membership ends
          and you’ll need to contact us to reactivate.
        </p>
        <Button
          variant="danger"
          disabled={busy || m.pending?.cancelAtPeriodEnd === true}
          onClick={() =>
            void confirm({
              title: 'Cancel membership',
              message: 'Cancel your membership at the end of this period?',
              confirmLabel: 'Cancel membership',
              danger: true,
            }).then((ok) => {
              if (ok) cancel.mutate();
            })
          }
        >
          {m.pending?.cancelAtPeriodEnd ? 'Cancellation scheduled' : 'Cancel membership'}
        </Button>
      </Card>
    </div>
  );
}

function PendingBanner({
  pending,
  disabled,
  onUndo,
}: {
  pending: NonNullable<Membership['pending']>;
  disabled: boolean;
  onUndo: () => void;
}) {
  const when = new Date(pending.effectiveAt).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md bg-primary/10 p-4 text-sm ring-1 ring-primary/20">
      <span className="flex-1">
        {pending.cancelAtPeriodEnd ? (
          <>
            Your membership ends on <span className="font-medium">{when}</span>.
          </>
        ) : (
          <>
            Your plan changes to{' '}
            <span className="font-medium">{pending.tier ? tierLabel(pending.tier) : ''}</span> on{' '}
            <span className="font-medium">{when}</span>.
          </>
        )}
      </span>
      <Button variant="secondary" disabled={disabled} onClick={onUndo}>
        {pending.cancelAtPeriodEnd ? 'Keep my membership' : 'Undo change'}
      </Button>
    </div>
  );
}

function PlanCard({
  entry,
  current,
  pendingTier,
  disabled,
  onChoose,
}: {
  entry: TierCatalogEntry;
  current: Tier;
  pendingTier: Tier | null;
  disabled: boolean;
  onChoose: () => void;
}) {
  const isCurrent = entry.tier === current;
  const isPending = pendingTier === entry.tier;
  const direction = tierDirection(current, entry.tier);

  return (
    <Card className={`flex flex-col gap-3 ${isCurrent ? 'ring-2 ring-primary' : ''}`}>
      <div className="flex items-center justify-between">
        <h2 className="font-title text-2xl tracking-wide">{tierLabel(entry.tier)}</h2>
        {isCurrent ? (
          <span className="rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-white">
            Current
          </span>
        ) : null}
      </div>

      <div className="text-sm">
        <div className="font-medium">{entry.workingHours}h development / month</div>
      </div>

      <ul className="flex flex-col gap-1 text-sm opacity-80">
        {entry.calls.length === 0 ? (
          <li className="opacity-60">No calls included</li>
        ) : (
          entry.calls.map((c) => (
            <li key={c.key} className="flex justify-between gap-2">
              <span>{c.label}</span>
              <span className="opacity-70">
                {c.maxPerMonth > 0 ? `${c.maxPerMonth}/month` : 'Upgrade'}
              </span>
            </li>
          ))
        )}
      </ul>

      <div className="mt-auto pt-2">
        {isCurrent ? (
          <Button variant="secondary" disabled>
            Your plan
          </Button>
        ) : isPending ? (
          <Button variant="secondary" disabled>
            Scheduled for the 1st
          </Button>
        ) : (
          <Button
            variant={direction === 'upgrade' ? 'primary' : 'secondary'}
            disabled={disabled}
            onClick={onChoose}
          >
            {direction === 'upgrade' ? 'Upgrade' : 'Downgrade'} to {tierLabel(entry.tier)}
          </Button>
        )}
      </div>
    </Card>
  );
}
